import { Maximize2, Star, Trash2 } from 'lucide-react';
import type { PropertyFileResponse } from '@/shared/api';
import { cn } from '@/shared/lib/cn';
import { AuthenticatedImage } from './AuthenticatedImage';

interface ImageTileProps {
  propertyId: string;
  image: PropertyFileResponse;
  isCover: boolean;
  onSetCover: () => void;
  onDelete: () => void;
  onOpen: () => void;
}

export function ImageTile({
  propertyId,
  image,
  isCover,
  onSetCover,
  onDelete,
  onOpen,
}: ImageTileProps) {
  return (
    <div
      className={cn(
        'bg-card-alt group relative aspect-square overflow-hidden rounded-md border',
        isCover ? 'ring-sage border-sage/50 ring-2' : 'border-line/60',
      )}
    >
      <button
        type="button"
        onClick={onOpen}
        aria-label={`${image.fileName} vergrößern`}
        className="absolute inset-0 block h-full w-full cursor-zoom-in"
      >
        <AuthenticatedImage
          propertyId={propertyId}
          fileId={image.id}
          variant="medium"
          alt={image.fileName}
          className="duration-medium ease-brand h-full w-full transition-transform group-hover:scale-[1.04]"
        />
      </button>

      {isCover && (
        <span className="bg-sage pointer-events-none absolute left-2 top-2 inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.08em] text-white">
          <Star className="size-2.5 fill-current" aria-hidden />
          Cover
        </span>
      )}

      <span className="bg-ink/55 pointer-events-none absolute right-2 top-2 flex size-7 items-center justify-center rounded-full text-white opacity-0 transition-opacity group-hover:opacity-100">
        <Maximize2 className="size-3.5" aria-hidden />
      </span>

      <div className="from-ink/85 bg-linear-to-t duration-fast ease-brand pointer-events-none absolute inset-x-0 bottom-0 flex translate-y-full items-center justify-between gap-2 to-transparent px-2.5 pb-2 pt-6 transition-transform group-hover:translate-y-0">
        {!isCover ? (
          <button
            type="button"
            onClick={onSetCover}
            className="pointer-events-auto inline-flex items-center gap-1 text-[11px] font-medium text-white/90 underline-offset-2 hover:text-white hover:underline"
          >
            <Star className="size-3" aria-hidden />
            Als Cover
          </button>
        ) : (
          <span className="pointer-events-none text-[11px] font-medium text-white/70">
            Cover-Bild
          </span>
        )}
        <button
          type="button"
          onClick={onDelete}
          aria-label="Löschen"
          className="pointer-events-auto ml-auto text-white/70 transition-colors hover:text-white"
        >
          <Trash2 className="size-3.5" aria-hidden />
        </button>
      </div>
    </div>
  );
}
